import React, { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { db } from "@/services/firebaseService";
import { Textarea } from "@/components/ui/textarea";
import { ApplyToAllButtonWithConfirm } from "../components/ApplyToAllButtonWithConfirm";
import { CampLocation } from "@/types/campLocation";
import { doc, getDoc } from "firebase/firestore";
import { Button } from "@/components/ui/button";
import { Pencil } from "lucide-react";
import { EditLocationSlugDialog } from "../components/EditLocationSlugDialog";
import { useAuth } from "@/context/AuthContext";
import { FieldOwnershipBadge } from "@/components/common/FieldOwnershipBadge";
import { FieldVendorHint } from "@/components/common/FieldVendorHint";
import { resolveCampPublicPage } from "@/utils/campPublicUrl";

function orgIdFromLocation(location: CampLocation): string | null {
  const p = location.providedByOrg as unknown;
  if (!p) return null;
  if (typeof p === "string") {
    const parts = p.split("/").filter(Boolean);
    return parts[parts.length - 1] || null;
  }
  if (typeof p === "object" && p !== null && "id" in p && typeof (p as { id: string }).id === "string") {
    return (p as { id: string }).id;
  }
  return null;
}

export const BasicInfoSection = ({
  form,
  location,
  isWelcomeScreen = false,
  onApplySearchResultExcerptToAll,
  applySearchResultExcerptToAllLoading = false,
  onPublicSlugSaved,
}: {
  form: any;
  location: CampLocation;
  isWelcomeScreen?: boolean;
  onApplySearchResultExcerptToAll?: (excerpt: string) => Promise<void>;
  applySearchResultExcerptToAllLoading?: boolean;
  onPublicSlugSaved?: () => void;
}) => {
  const { isAdmin } = useAuth();
  const [orgSlug, setOrgSlug] = useState<string>("");
  const [slugDialogOpen, setSlugDialogOpen] = useState(false);

  const orgId = orgIdFromLocation(location);
  const locSlug = typeof location.slug === "string" ? location.slug.trim() : "";
  const showSlugRow = isAdmin && !isWelcomeScreen;
  const campPublicPage = resolveCampPublicPage(location.slugPath);
  const campPublicUrlDisplay =
    campPublicPage.kind === "url"
      ? campPublicPage.url
      : campPublicPage.kind === "none"
        ? "Not assigned yet"
        : "Not available";
  const canEditPublicSlug = showSlugRow && Boolean(orgId);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!orgId) {
        setOrgSlug("");
        return;
      }
      try {
        const snap = await getDoc(doc(db, "campOrganizations", orgId));
        const s = snap.exists() ? (snap.get("slug") as string | undefined) : undefined;
        if (!cancelled) setOrgSlug(typeof s === "string" ? s.trim() : "");
      } catch {
        if (!cancelled) setOrgSlug("");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-center text-2xl">Basic Information</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }: any) => (
            <FormItem>
              <div className="flex items-center gap-2">
                <FormLabel className="text-left block">Camp Location Name</FormLabel>
                <FieldOwnershipBadge fieldPath="/name" />
              </div>
              <FormControl>
                <Input {...field} disabled={isWelcomeScreen} />
              </FormControl>
              <FieldVendorHint fieldPath="/name" onUseVendorValue={(v) => form.setValue("name", v as string)} />
              {showSlugRow ? (
                <>
                  <div className="mt-1 flex w-full flex-wrap items-center justify-end gap-x-1 gap-y-0.5 text-right text-sm text-muted-foreground">
                    <span>Slug:</span>
                    <span className="font-mono text-foreground">
                      {locSlug ? locSlug : "(not set)"}
                    </span>
                    {canEditPublicSlug ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
                        aria-label="Edit location URL slug"
                        title="Edit location URL slug"
                        onClick={() => setSlugDialogOpen(true)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    ) : null}
                  </div>
                  {!orgSlug && locSlug ? (
                    <p className="text-xs text-amber-800 dark:text-amber-200">
                      Your organization does not have a slug yet. Set an organization slug before this location can
                      appear in full public URLs.
                    </p>
                  ) : null}
                </>
              ) : null}
              {isWelcomeScreen && (
                <FormDescription className="text-left">
                  Contact support@kidsbooked.com to change this field.
                </FormDescription>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
        {canEditPublicSlug ? (
          <EditLocationSlugDialog
            open={slugDialogOpen}
            onOpenChange={setSlugDialogOpen}
            locationId={location.id}
            orgId={orgId}
            initialSlug={location.slug}
            onSaved={onPublicSlugSaved}
          />
        ) : null}
        <FormField control={form.control} name="urlMain" render={({
        field
      }: any) => <FormItem className="pt-2">
              <div className="flex items-center gap-2">
                <FormLabel className="text-left block">Main URL</FormLabel>
                <FieldOwnershipBadge fieldPath="/urlMain" />
              </div>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FieldVendorHint fieldPath="/urlMain" onUseVendorValue={(v) => form.setValue("urlMain", v as string)} />
              <FormDescription className="text-left">URL of the camp location's main page on your website</FormDescription>
              <FormMessage />
            </FormItem>} />
        {isWelcomeScreen && (
          <FormItem className="pt-2">
            <FormLabel className="text-left block">Camp Location URL on KidsBooked</FormLabel>
            <FormControl>
              <Input value={campPublicUrlDisplay} readOnly disabled />
            </FormControl>
            <FormDescription className="text-left">
              Contact support@kidsbooked.com to change this field.
            </FormDescription>
          </FormItem>
        )}
        <FormField 
          control={form.control} 
          name="searchResultExcerpt" 
          render={({ field }: any) => (
            <FormItem className="pt-2">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <FormLabel className="text-left block">Search Result Excerpt</FormLabel>
                  <FieldOwnershipBadge fieldPath="/searchResultExcerpt" />
                </div>
                {onApplySearchResultExcerptToAll && !isWelcomeScreen && (
                  <ApplyToAllButtonWithConfirm
                    disabled={applySearchResultExcerptToAllLoading}
                    dialogTitle="Apply to all locations?"
                    dialogDescription="This will overwrite Search Result Excerpt for every location that shares the same camp organization. This updates drafts and cannot be undone."
                    onConfirm={async () => {
                      const excerpt =
                        typeof form?.getValues === "function" ? (form.getValues("searchResultExcerpt") ?? "") : "";
                      await onApplySearchResultExcerptToAll(String(excerpt ?? ""));
                    }}
                  />
                )}
              </div>
              <FormControl>
                <Textarea 
                  {...field} 
                  maxLength={50} 
                  placeholder="A short description that will appear in search results"
                  className="resize-none h-[40px] min-h-[40px] py-2 overflow-hidden" 
                  rows={1}
                  onInput={(e) => {
                    // Ensure the input doesn't get taller than one row
                    const target = e.target as HTMLTextAreaElement;
                    target.style.height = 'auto';
                    target.style.height = '40px';
                  }}
                  disabled={isWelcomeScreen}
                />
              </FormControl>
              <FieldVendorHint fieldPath="/searchResultExcerpt" onUseVendorValue={(v) => form.setValue("searchResultExcerpt", v as string)} />
              <FormDescription className="text-left">
                A short & sweet line capturing the essence of your camp; will display on search results. Max. 50 characters. {field.value?.length || 0}/50
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </CardContent>
    </Card>
  );
};

